import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Send, Loader2, FileText, X, CheckCheck, Check } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImageUrl } from "@/lib/api";

const POLL_MS = 3000;
const IMG_RE = /\.(jpe?g|png|webp|gif)$/i;

export const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export async function uploadSupportFile(file) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/support/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
}

export function AttachmentList({ items, mine }) {
    if (!items?.length) return null;
    return (
        <div className="mt-1.5 space-y-1.5">
            {items.map((a) => IMG_RE.test(a.url) ? (
                <a key={a.url} href={resolveImageUrl(a.url)} target="_blank" rel="noreferrer" data-testid="support-attachment-image">
                    <img src={resolveImageUrl(a.url)} alt={a.name} className="max-h-44 rounded-lg border border-black/10" />
                </a>
            ) : (
                <a key={a.url} href={resolveImageUrl(a.url)} target="_blank" rel="noreferrer" data-testid="support-attachment-file" className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${mine ? "bg-white/15 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>
                    <FileText className="h-4 w-4 flex-shrink-0" /> <span className="truncate">{a.name}</span>
                </a>
            ))}
        </div>
    );
}

function MessageBubble({ m, mine, read }) {
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.id}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-sky-600 text-white rounded-br-md" : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-md"}`}>
                {!mine && <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-0.5">{m.sender_name}</div>}
                {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                <AttachmentList items={m.attachments} mine={mine} />
                <div className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>
                    {fmtTime(m.created_at)}
                    {mine && (read ? <CheckCheck data-testid="support-msg-read" className="h-3.5 w-3.5 text-emerald-300" /> : <Check className="h-3.5 w-3.5" />)}
                </div>
            </div>
        </div>
    );
}

function useTicketPolling(ticketId, onTicket) {
    const [messages, setMessages] = useState([]);
    const [ticket, setTicket] = useState(null);
    const lastRef = useRef("");

    const fetchNew = useCallback(async () => {
        const r = await api.get(`/support/tickets/${ticketId}`, { params: lastRef.current ? { after: lastRef.current } : {} });
        const msgs = r.data.messages || [];
        if (msgs.length) {
            lastRef.current = msgs[msgs.length - 1].created_at;
            setMessages((prev) => {
                const seen = new Set(prev.map((x) => x.id));
                return [...prev, ...msgs.filter((x) => !seen.has(x.id))];
            });
        }
        setTicket(r.data.ticket);
        onTicket?.(r.data.ticket);
    }, [ticketId, onTicket]);

    useEffect(() => {
        lastRef.current = "";
        setMessages([]);
        setTicket(null);
        fetchNew().catch(() => {});
        const t = setInterval(() => { if (!document.hidden) fetchNew().catch(() => {}); }, POLL_MS);
        return () => clearInterval(t);
    }, [ticketId, fetchNew]);

    return { messages, ticket, fetchNew };
}

export function SupportChatThread({ ticketId, side, onTicket, disabled }) {
    const { messages, ticket, fetchNew } = useTicketPolling(ticketId, onTicket);
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);
    const typingRef = useRef(0);
    const fileRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, ticket?.other_typing]);

    const onType = (v) => {
        setText(v);
        if (Date.now() - typingRef.current > 3000) {
            typingRef.current = Date.now();
            api.post(`/support/tickets/${ticketId}/typing`).catch(() => {});
        }
    };

    const onPick = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = "";
        setBusy(true);
        try {
            const uploaded = [];
            for (const f of list) uploaded.push(await uploadSupportFile(f));
            setFiles((p) => [...p, ...uploaded]);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Dosya yüklenemedi");
        } finally {
            setBusy(false);
        }
    };

    const send = async () => {
        if (!text.trim() && !files.length) return;
        setBusy(true);
        try {
            await api.post(`/support/tickets/${ticketId}/messages`, { text, attachments: files });
            setText("");
            setFiles([]);
            await fetchNew();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Mesaj gönderilemedi");
        } finally {
            setBusy(false);
        }
    };

    const otherRead = ticket?.other_last_read;
    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-slate-50 dark:bg-slate-950/40" data-testid="support-thread">
                {messages.map((m) => <MessageBubble key={m.id} m={m} mine={m.sender_side === side} read={!!otherRead && otherRead >= m.created_at} />)}
                {ticket?.other_typing && (
                    <div data-testid="support-typing" className="text-xs text-slate-500 italic px-1 flex items-center gap-1.5">
                        <span className="inline-flex gap-0.5"><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" /><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:120ms]" /><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:240ms]" /></span>
                        {side === "hotel" ? "Destek ekibi yazıyor…" : "Otel yazıyor…"}
                    </div>
                )}
                <div ref={endRef} />
            </div>
            {!disabled && (
                <div className="border-t border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-900">
                    {files.filter(Boolean).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {files.filter(Boolean).map((f) => (
                                <span key={f.url} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs">
                                    <FileText className="h-3 w-3" /> <span className="max-w-[140px] truncate">{f.name}</span>
                                    <button onClick={() => setFiles((p) => p.filter((x) => x !== f))} className="p-0.5"><X className="h-3 w-3" /></button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="support-file-input" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="support-attach-btn" className="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Dosya / görsel ekle"><Paperclip className="h-4 w-4" /></button>
                        <textarea
                            data-testid="support-message-input"
                            value={text}
                            onChange={(e) => onType(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            rows={1}
                            placeholder="Mesajınızı yazın… (Enter: gönder)"
                            className="flex-1 resize-none max-h-32 px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm outline-none text-slate-900 dark:text-white"
                        />
                        <button onClick={send} disabled={busy} data-testid="support-send-btn" className="p-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
